// Changing your password, in User settings. Only someone who signs in with a
// password sees it; single sign-on users manage theirs with their provider.
import { useCallback, useState, useSyncExternalStore } from "react";
import { restPost } from "../../lib/restClient.js";
import { getAuthIdentityEpoch, getUserMetadata, onAuthIdentityChange } from "../../lib/authTokenStore.js";
import Button from "../ui/Button.jsx";
import Field from "../ui/Field.jsx";
import { SaveStatus } from "../ui/Notice.jsx";
import { FIELD } from "../ui/styles.js";
import { PAGE_PART } from "./Page.jsx";

export default function PasswordSection() {
    useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
    if (getUserMetadata()?.authenticationMethod !== "AUTHENTICATION_METHOD_PASSWORD") return null;
    return <PasswordForm />;
}

function PasswordForm() {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);

    const onSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (next.length < 8) {
            setError("New password must be at least 8 characters.");
            return;
        }
        if (next !== confirm) {
            setError("New password and confirmation don't match.");
            return;
        }
        setStatus("saving");
        setError(null);
        try {
            await restPost("/v1/user-management/change-password", {
                current_password: current,
                new_password: next,
            });
            setCurrent(""); setNext(""); setConfirm("");
            setStatus("saved");
            setTimeout(() => setStatus(null), 2000);
        } catch (err) {
            setStatus(null);
            setError(err.message || String(err));
        }
    }, [current, next, confirm]);

    const canSubmit = current.length > 0 && next.length > 0 && confirm.length > 0;

    return (
        <section id="settings-password" aria-labelledby="settings-password-title" className="grid max-w-[480px] gap-[14px]">
            <div className="grid gap-[4px]">
                <h2 id="settings-password-title" className={PAGE_PART}>Change password</h2>
                <p className="m-0 text-[13px] leading-[1.45] text-[color:var(--muted)]">
                    This only applies if you sign in with a password. If you use single sign-on, manage your credentials with your identity provider.
                </p>
            </div>
            <form id="settings-password-form" onSubmit={onSubmit} className="grid gap-[14px]">
                <Field label="Current password">
                    <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className={FIELD} />
                </Field>
                <Field label="New password" hint="At least 8 characters.">
                    <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className={FIELD} />
                </Field>
                <Field label="Confirm new password">
                    <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={FIELD} />
                </Field>
                <div className="flex items-center justify-end gap-[12px]">
                    <SaveStatus status={status} error={error} savedLabel="Password updated" />
                    <Button type="submit" variant={canSubmit ? "primary" : "secondary"} disabled={!canSubmit || status === "saving"}>
                        Update password
                    </Button>
                </div>
            </form>
        </section>
    );
}
